/**
 * PortalStatusChip - the customer's Portalstatus next to the Mandatskacheln (Stefan 10.5).
 *
 * The state and its label are API-computed (GET /customers/:t/:id/portal-status); this chip only
 * adds the dates on the Swiss calendar day and colours by state.
 */

import { formatZurichDateDe, portalReadOnlyUntilLabel } from '@brokly/shared';
import type { PortalStatusDto, PortalStatusState } from '@brokly/shared';

const STATE_CLASSES: Record<PortalStatusState, string> = {
  kein_zugang: 'bg-muted text-muted-foreground',
  eingeladen: 'bg-sky-100 text-sky-900 dark:bg-sky-950/40 dark:text-sky-200',
  link_abgelaufen: 'bg-orange-100 text-orange-900 dark:bg-orange-950/40 dark:text-orange-200',
  aktiv: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200',
  portal_deaktiviert: 'bg-muted text-muted-foreground',
  zugang_lesend: 'bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
  zugang_deaktiviert: 'bg-muted text-muted-foreground',
};

/** The chip text: the label plus the dates the state carries. */
export function portalStatusText(status: PortalStatusDto): string {
  const day = (v: string | null) => (v !== null ? formatZurichDateDe(v) : null);
  if (status.state === 'eingeladen' && !status.accountExists) {
    const parts = [day(status.invitedAt) ? `Eingeladen am ${day(status.invitedAt)}` : status.label];
    if (day(status.linkValidUntil)) parts.push(`Link gültig bis ${day(status.linkValidUntil)}`);
    return parts.join(' · ');
  }
  // P6-S7: the run-out of an inaktiv customer's access: «Portal lesend bis 31.03.2027», the last readable
  // day (the Zurich day before readOnlyUntil, the instant the access runs out).
  if (status.state === 'zugang_lesend') {
    return portalReadOnlyUntilLabel(status.label, status.readOnlyUntil ?? null);
  }
  if (status.state === 'aktiv' && day(status.activatedAt)) {
    return `Kundenportal aktiv seit ${day(status.activatedAt)}`;
  }
  return status.label;
}

export function PortalStatusChip({ status }: { status: PortalStatusDto }) {
  const text = portalStatusText(status);
  return (
    <span
      data-testid="portal-status-chip"
      title="Kundenportal"
      className={`rounded-md px-2 py-1 text-xs font-medium ${STATE_CLASSES[status.state]}`}
    >
      {text}
    </span>
  );
}
